import { useState, useEffect, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Download, X, FileText, Camera, Mail } from "lucide-react";
import { IsbnScannerModal, TitleAutocomplete, ExtractFromImageButton, EmailInvoiceDialog } from "@/pages/admin/InvoiceHelpers";

const SYMBOLS = { INR: "\u20b9", USD: "$", GBP: "\u00a3", EUR: "\u20ac" };
const CURRENCIES = ["INR", "USD", "GBP", "EUR"];

const EMPTY_ITEM = () => ({ isbn: "", title: "", author: "", publisher: "", qty: 1, mrp: 0, discount_percent: 0 });

const EMPTY_REFS = () => ({ ref_no: "", po_number: "", po_date: "", delivery_challan_no: "", order_date: "" });
const EMPTY_COMPANY = () => ({
  name: "", address: "", phone: "", email: "", pan: "",
  bank_account_name: "", bank_account_number: "", bank_name: "", bank_branch: "", bank_ifsc: "",
  logo_url: "", signature_url: "",
});

const EMPTY_DOC = (defaultBankDetails) => ({
  customer: { name: "", email: "", phone: "", address: "", city: "", state: "", country: "India", pincode: "", gstin: "" },
  currency: "INR",
  items: [EMPTY_ITEM()],
  shipping_cost: 0,
  extra_discount: 0,
  notes: "",
  show_bank_details: defaultBankDetails,
  references: EMPTY_REFS(),
  company_override: EMPTY_COMPANY(),
});

function computePreview(doc) {
  let subtotal = 0;
  const rows = (doc.items || []).map((it) => {
    const mrp = Number(it.mrp) || 0;
    const disc = Number(it.discount_percent) || 0;
    const qty = Number(it.qty) || 0;
    const price = +(mrp * (1 - disc / 100)).toFixed(2);
    const amount = +(price * qty).toFixed(2);
    subtotal += amount;
    return { price, amount };
  });
  const shipping = Number(doc.shipping_cost) || 0;
  const extra = Number(doc.extra_discount) || 0;
  const total = +(subtotal + shipping - extra).toFixed(2);
  return { rows, subtotal: +subtotal.toFixed(2), total };
}

/**
 * Admin panel for Manual Invoices (kind="MI") or Proforma Invoices (kind="PI").
 * kind determines the counter, doc-number suffix, and default bank-details flag.
 */
export default function InvoiceDocsPanel({ kind, title }) {
  const isMI = kind === "MI";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [modal, setModal] = useState(null); // {id?, doc} or null
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [emailFor, setEmailFor] = useState(null); // an invoice doc when the email dialog is open

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/invoices/${kind}`, { params: { limit: 100, q: q || undefined } });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(apiError(e)); }
  }, [kind, q]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => setModal({ id: null, doc: EMPTY_DOC(isMI) });
  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/admin/invoices/${kind}/${id}`);
      setModal({
        id,
        doc: {
          customer: data.customer || EMPTY_DOC(isMI).customer,
          currency: data.currency || "INR",
          items: data.items?.length ? data.items : [EMPTY_ITEM()],
          shipping_cost: data.shipping_cost || 0,
          extra_discount: data.extra_discount || 0,
          notes: data.notes || "",
          show_bank_details: data.show_bank_details ?? isMI,
          references: { ...EMPTY_REFS(), ...(data.references || {}) },
          company_override: { ...EMPTY_COMPANY(), ...(data.company_override || {}) },
        },
      });
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id, doc_number) => {
    if (!window.confirm(`Delete ${doc_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/invoices/${kind}/${id}`);
      toast.success(`Deleted ${doc_number}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadPdf = async (id, doc_number) => {
    try {
      const res = await api.get(`/admin/invoices/${kind}/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${doc_number.replace(/\//g, "_")}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    if (!modal) return;
    const d = modal.doc;
    // Client-side validation echo
    if (!d.customer.name?.trim()) { toast.error("Customer name is required"); return; }
    if (!d.items?.length || d.items.some((i) => !i.title?.trim())) { toast.error("All items must have a title"); return; }
    setBusy(true);
    try {
      const cleanCustomer = { ...d.customer };
      // Convert empty strings to null for optional fields so backend EmailStr accepts them
      if (!cleanCustomer.email?.trim()) cleanCustomer.email = null;
      // Strip fully-blank company override so backend uses the defaults —
      // otherwise every unchecked field would overwrite the header with "".
      const co = d.company_override || {};
      const hasOverride = Object.values(co).some((v) => (v || "").toString().trim());
      const payload = {
        ...d,
        customer: cleanCustomer,
        items: d.items.map((i) => ({ ...i, qty: Number(i.qty), mrp: Number(i.mrp), discount_percent: Number(i.discount_percent) })),
        references: d.references || {},
        company_override: hasOverride ? co : null,
      };
      if (modal.id) await api.put(`/admin/invoices/${kind}/${modal.id}`, payload);
      else await api.post(`/admin/invoices/${kind}`, payload);
      toast.success(modal.id ? "Invoice updated" : "Invoice created");
      setModal(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#e7e5e4] bg-white" data-testid={`panel-${kind.toLowerCase()}`}>
      <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">{title}</div>
          <div className="text-lg font-black">{total} document{total === 1 ? "" : "s"}</div>
        </div>
        <div className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search doc # or customer" className="border-2 border-[#e4e4e7] px-3 py-2 text-sm w-56" data-testid={`${kind}-search`} />
          <button onClick={openCreate} className="bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold px-4 py-2 text-sm flex items-center gap-1.5" data-testid={`${kind}-new`}>
            <Plus className="w-4 h-4" /> New Invoice
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#f4f4f5] uppercase text-[10px] tracking-wider">
            <tr>
              <th className="p-3 text-left">Doc #</th>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Customer</th>
              <th className="p-3 text-right">Total</th>
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-t border-border" data-testid={`${kind}-row-${d.doc_number.replace(/\//g,'_')}`}>
                <td className="p-3 font-mono font-bold">{d.doc_number}</td>
                <td className="p-3 text-xs text-[#71717a]">{d.created_at ? new Date(d.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                <td className="p-3">{d.customer?.name}</td>
                <td className="p-3 text-right font-mono">{SYMBOLS[d.currency] || d.currency} {Number(d.total).toFixed(2)}</td>
                <td className="p-3 text-center whitespace-nowrap">
                  <button onClick={() => openEdit(d.id)} className="p-1.5 hover:bg-[#f4f4f5]" title="Edit" data-testid={`${kind}-edit-${d.doc_number.replace(/\//g,'_')}`}><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => downloadPdf(d.id, d.doc_number)} className="p-1.5 hover:bg-[#f4f4f5]" title="Download PDF" data-testid={`${kind}-pdf-${d.doc_number.replace(/\//g,'_')}`}><Download className="w-4 h-4" /></button>
                  <button onClick={() => setEmailFor(d)} className="p-1.5 hover:bg-[#fff7ed] text-[#ea580c]" title="Email invoice" data-testid={`${kind}-email-${d.doc_number.replace(/\//g,'_')}`}><Mail className="w-4 h-4" /></button>
                  <button onClick={() => remove(d.id, d.doc_number)} className="p-1.5 hover:bg-[#fee2e2] text-[#dc2626]" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-[#71717a] text-sm"><FileText className="w-8 h-8 mx-auto mb-2 text-[#a1a1aa]" />No {title.toLowerCase()} yet — click <b>New Invoice</b> to create one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && <InvoiceModal kind={kind} title={title} modal={modal} setModal={setModal} save={save} busy={busy} />}
      <EmailInvoiceDialog
        open={!!emailFor}
        onClose={() => setEmailFor(null)}
        endpoint={emailFor ? `/admin/invoices/${kind}/${emailFor.id}/email` : ""}
        defaultTo={emailFor?.customer?.email || ""}
        docNumber={emailFor?.doc_number || ""}
      />
    </div>
  );
}

function InvoiceModal({ kind, title, modal, setModal, save, busy }) {
  const isMI = kind === "MI";
  const doc = modal.doc;
  const setDoc = (patch) => setModal((m) => ({ ...m, doc: { ...m.doc, ...patch } }));
  const setCust = (patch) => setDoc({ customer: { ...doc.customer, ...patch } });
  const setItem = (idx, patch) => {
    const items = doc.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setDoc({ items });
  };
  const addItem = () => setDoc({ items: [...doc.items, EMPTY_ITEM()] });
  const removeItem = (idx) => setDoc({ items: doc.items.filter((_, i) => i !== idx) });
  const preview = computePreview(doc);
  const sym = SYMBOLS[doc.currency] || doc.currency;
  const [scannerOpen, setScannerOpen] = useState(false);

  // Auto-fill title/author/mrp from books DB when a full ISBN is entered.
  // User can still edit — we ONLY fill blank fields to preserve manual overrides.
  const lookupIsbn = async (idx, rawIsbn) => {
    const clean = (rawIsbn || "").replace(/[^0-9Xx]/g, "");
    if (clean.length < 10) return;
    try {
      const { data } = await api.get(`/books/by-isbn/${clean}`);
      if (!data) return;
      applyBookToItem(idx, data);
    } catch (e) { /* silent — 404 means unknown ISBN, user will type manually */ }
  };

  const applyBookToItem = (idx, book) => {
    if (!book) return;
    const cur = doc.items[idx] || {};
    const patch = {};
    if (!cur.title?.trim() && book.title) patch.title = book.title;
    if (!cur.author?.trim() && book.author) patch.author = book.author;
    if (!cur.publisher?.trim() && book.publisher) patch.publisher = book.publisher;
    if (!cur.isbn?.trim() && book.isbn) patch.isbn = book.isbn;
    const mrpNow = Number(cur.mrp) || 0;
    if (mrpNow === 0 && Number(book.mrp || book.price) > 0) patch.mrp = book.mrp || book.price;
    if (Object.keys(patch).length) {
      setItem(idx, patch);
      toast.success(`Auto-filled from catalog: ${book.title}`);
    }
  };

  const handleScanned = (isbn) => {
    // Find first empty row OR add a new row and set isbn + trigger lookup
    let idx = doc.items.findIndex((it) => !it.isbn?.trim() && !it.title?.trim());
    let items = [...doc.items];
    if (idx === -1) {
      items = [...items, { ...EMPTY_ITEM(), isbn }];
      idx = items.length - 1;
      setDoc({ items });
    } else {
      items[idx] = { ...items[idx], isbn };
      setDoc({ items });
    }
    setTimeout(() => lookupIsbn(idx, isbn), 100);
    setScannerOpen(false);
  };

  const pickFromCatalog = (book) => {
    // Add a new item pre-filled from picked book, OR fill first empty row
    const items = [...doc.items];
    const emptyIdx = items.findIndex((it) => !it.isbn?.trim() && !it.title?.trim());
    const built = { isbn: book.isbn || "", title: book.title || "", author: book.author || "", publisher: book.publisher || "", mrp: book.mrp || book.price || 0 };
    if (emptyIdx >= 0) {
      items[emptyIdx] = { ...items[emptyIdx], ...built };
    } else {
      items.push({ ...EMPTY_ITEM(), ...built });
    }
    setDoc({ items });
    toast.success(`Added: ${book.title}`);
  };

  const handleExtracted = (extracted) => {
    // Map LLM output shape → MI/PI item shape (mrp + discount_percent).
    const mapped = extracted.map((it) => ({
      isbn: it.isbn || "",
      title: it.title || "",
      author: it.author || "",
      publisher: it.publisher || "",
      qty: Number(it.qty) || 1,
      mrp: Number(it.mrp ?? it.price) || 0,
      discount_percent: Number(it.discount_percent) || 0,
    }));
    // Replace items if the current list is empty/placeholder, else append
    const isPlaceholder = doc.items.length === 1 && !doc.items[0].title?.trim() && !doc.items[0].isbn?.trim();
    setDoc({ items: isPlaceholder ? mapped : [...doc.items, ...mapped] });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 overflow-y-auto" onClick={() => !busy && setModal(null)} data-testid={`${kind}-modal`}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-6xl border-2 border-[#09090b] my-6 max-h-[92vh] overflow-y-auto">
        <div className="p-4 border-b-2 border-[#09090b] flex items-center justify-between bg-[#fff7ed] sticky top-0 z-10">
          <div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#ea580c]">{modal.id ? "Edit" : "New"} {title}</div>
            <div className="font-display text-xl font-black">Editable Preview</div>
          </div>
          <button onClick={() => !busy && setModal(null)} className="p-1" data-testid={`${kind}-modal-close`}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* References strip — institutional buyers need these */}
          <RefBlock refs={doc.references || {}} onChange={(refs) => setDoc({ references: refs })} kind={kind} />

          {/* Company override toggle */}
          <CompanyOverrideBlock override={doc.company_override || {}} onChange={(co) => setDoc({ company_override: co })} kind={kind} />

          {/* Customer + Currency */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Section title="Bill To">
              <Field label="Name *" value={doc.customer.name} onChange={(v) => setCust({ name: v })} testid="cust-name" />
              <Field label="Email" value={doc.customer.email} onChange={(v) => setCust({ email: v })} type="email" />
              <Field label="Phone" value={doc.customer.phone} onChange={(v) => setCust({ phone: v })} />
              <Field label="GSTIN (optional)" value={doc.customer.gstin} onChange={(v) => setCust({ gstin: v })} />
            </Section>
            <Section title="Shipping Address">
              <Field label="Address" value={doc.customer.address} onChange={(v) => setCust({ address: v })} />
              <div className="grid grid-cols-2 gap-2">
                <Field label="City" value={doc.customer.city} onChange={(v) => setCust({ city: v })} />
                <Field label="State" value={doc.customer.state} onChange={(v) => setCust({ state: v })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Country" value={doc.customer.country} onChange={(v) => setCust({ country: v })} />
                <Field label="Pincode/ZIP" value={doc.customer.pincode} onChange={(v) => setCust({ pincode: v })} />
              </div>
            </Section>
            <Section title="Invoice Settings">
              <label className="block">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">Currency</div>
                <select value={doc.currency} onChange={(e) => setDoc({ currency: e.target.value })} className="w-full border-2 border-[#e4e4e7] px-3 py-2 text-sm outline-none" data-testid={`${kind}-currency`}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c} ({SYMBOLS[c]})</option>)}
                </select>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={doc.show_bank_details} onChange={(e) => setDoc({ show_bank_details: e.target.checked })} className="mt-1" data-testid={`${kind}-bank-toggle`} />
                <span>Show bank account details in footer{isMI ? " (recommended for offline payments)" : ""}</span>
              </label>
              <Field label="Terms & Conditions" value={doc.notes} onChange={(v) => setDoc({ notes: v })} multiline />
            </Section>
          </div>

          {/* Items table (fully editable) */}
          <Section title={`Items (${doc.items.length})`}>
            <div className="flex flex-wrap gap-2 mb-2 items-center">
              <button type="button" onClick={() => setScannerOpen(true)} className="border-2 border-[#09090b] hover:bg-[#09090b] hover:text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid={`${kind}-scan-isbn`}>
                <Camera className="w-3.5 h-3.5" /> Scan ISBN
              </button>
              <ExtractFromImageButton kind={kind} onExtracted={handleExtracted} testid={`${kind}-extract-image`} />
              <TitleAutocomplete onPick={pickFromCatalog} testid={`${kind}-title-search`} />
              <div className="text-[11px] text-[#71717a] ml-auto">Upload bill photo · scan ISBN · or search catalog</div>
            </div>
            <div className="overflow-x-auto border border-border">
              <table className="text-sm" style={{ minWidth: '1200px', width: '100%' }}>
                <thead className="bg-[#09090b] text-white uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="p-2 w-8">#</th>
                    <th className="p-2 text-left w-24">ISBN</th>
                    <th className="p-2 text-left" style={{ minWidth: '240px' }}>Book Title *</th>
                    <th className="p-2 text-left w-28">Author</th>
                    <th className="p-2 text-left w-28">Publisher</th>
                    <th className="p-2 w-14">Qty</th>
                    <th className="p-2 w-20">MRP ({sym})</th>
                    <th className="p-2 w-14">Disc %</th>
                    <th className="p-2 w-20 text-right">Price</th>
                    <th className="p-2 w-20 text-right">Amount</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {doc.items.map((it, i) => (
                    <tr key={i} className="border-t border-border" data-testid={`${kind}-item-${i}`}>
                      <td className="p-2 text-center text-xs text-[#71717a]">{i + 1}</td>
                      <td className="p-1"><input value={it.isbn} onChange={(e) => setItem(i, { isbn: e.target.value })} onBlur={(e) => lookupIsbn(i, e.target.value)} placeholder="ISBN (auto-fills)" className="w-full px-2 py-1 border border-[#e4e4e7] text-xs" data-testid={`${kind}-item-isbn-${i}`} /></td>
                      <td className="p-1"><input value={it.title} onChange={(e) => setItem(i, { title: e.target.value })} required placeholder="e.g. Introduction to Sanskrit" className="w-full px-2 py-1 border border-[#e4e4e7] text-xs" data-testid={`${kind}-item-title-${i}`} /></td>
                      <td className="p-1"><input value={it.author} onChange={(e) => setItem(i, { author: e.target.value })} placeholder="Author Name" className="w-full px-2 py-1 border border-[#e4e4e7] text-xs" /></td>
                      <td className="p-1"><input value={it.publisher || ""} onChange={(e) => setItem(i, { publisher: e.target.value })} placeholder="Publisher" className="w-full px-2 py-1 border border-[#e4e4e7] text-xs" data-testid={`${kind}-item-publisher-${i}`} /></td>
                      <td className="p-1"><input type="number" min="1" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} className="w-full px-2 py-1 border border-[#e4e4e7] text-xs text-center" /></td>
                      <td className="p-1"><input type="number" min="0" step="0.01" value={it.mrp} onChange={(e) => setItem(i, { mrp: e.target.value })} className="w-full px-2 py-1 border border-[#e4e4e7] text-xs text-right" /></td>
                      <td className="p-1"><input type="number" min="0" max="100" step="0.1" value={it.discount_percent} onChange={(e) => setItem(i, { discount_percent: e.target.value })} className="w-full px-2 py-1 border border-[#e4e4e7] text-xs text-center" /></td>
                      <td className="p-2 text-right font-mono">{preview.rows[i]?.price?.toFixed(2)}</td>
                      <td className="p-2 text-right font-mono font-bold">{preview.rows[i]?.amount?.toFixed(2)}</td>
                      <td className="p-1 text-center">
                        {doc.items.length > 1 && (
                          <button type="button" onClick={() => removeItem(i)} className="p-1 hover:bg-[#fee2e2] text-[#dc2626]"><X className="w-3.5 h-3.5" /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" onClick={addItem} className="mt-2 border-2 border-dashed border-[#e4e4e7] hover:border-[#ea580c] px-3 py-2 text-xs font-bold uppercase tracking-wider w-full" data-testid={`${kind}-add-item`}>
              <Plus className="w-3 h-3 inline" /> Add another item
            </button>
          </Section>

          {/* Totals + shipping */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title="Charges & Discount">
              <Field label={`Shipping (${sym})`} value={doc.shipping_cost} onChange={(v) => setDoc({ shipping_cost: v })} type="number" />
              <Field label={`Extra discount (${sym})`} value={doc.extra_discount} onChange={(v) => setDoc({ extra_discount: v })} type="number" />
            </Section>
            <div className="border border-[#09090b] bg-[#f4f4f5] p-4 text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-[#71717a]">Subtotal</span><span className="font-mono">{sym} {preview.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#71717a]">Shipping</span><span className="font-mono">{sym} {(Number(doc.shipping_cost) || 0).toFixed(2)}</span></div>
              {Number(doc.extra_discount) > 0 && (
                <div className="flex justify-between"><span className="text-[#71717a]">Extra discount</span><span className="font-mono">- {sym} {Number(doc.extra_discount).toFixed(2)}</span></div>
              )}
              <div className="flex justify-between pt-2 border-t-2 border-[#09090b] text-lg font-black">
                <span>NET PAYABLE</span>
                <span className="font-mono text-[#ea580c]" data-testid={`${kind}-total`}>{sym} {preview.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t-2 border-[#09090b] flex items-center justify-end gap-2 bg-white sticky bottom-0">
          <button onClick={() => setModal(null)} disabled={busy} className="px-4 py-2 text-sm font-bold border-2 border-[#e4e4e7] hover:border-[#09090b] disabled:opacity-50" data-testid={`${kind}-modal-cancel`}>Cancel</button>
          <button onClick={save} disabled={busy} className="px-5 py-2 text-sm font-bold bg-[#ea580c] hover:bg-[#c2410c] text-white disabled:opacity-60" data-testid={`${kind}-modal-save`}>
            {busy ? "Saving…" : (modal.id ? "Save & Regenerate" : "Create Invoice")}
          </button>
        </div>
      </div>

      {scannerOpen && <IsbnScannerModal onClose={() => setScannerOpen(false)} onScanned={handleScanned} />}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-border p-3">
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-2">{title}</div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", multiline = false, testid }) {
  const common = "w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-1.5 text-sm outline-none";
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">{label}</div>
      {multiline ? (
        <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${common} min-h-[68px]`} data-testid={testid} />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={testid} />
      )}
    </label>
  );
}


// ------- Institutional Reference Block (Ref No / PO / Delivery / Order Date) -------
export function RefBlock({ refs, onChange, kind = "" }) {
  const set = (patch) => onChange({ ...refs, ...patch });
  return (
    <div className="border-2 border-dashed border-[#fed7aa] bg-[#fff7ed] p-3">
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-2">References (Library / Institutional Buyers)</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <MiniField label="Ref No." value={refs.ref_no} onChange={(v) => set({ ref_no: v })} testid={`${kind}-ref-no`} />
        <MiniField label="PO Number" value={refs.po_number} onChange={(v) => set({ po_number: v })} testid={`${kind}-po-number`} />
        <MiniField label="PO Date" value={refs.po_date} onChange={(v) => set({ po_date: v })} type="date" testid={`${kind}-po-date`} />
        <MiniField label="Delivery Challan" value={refs.delivery_challan_no} onChange={(v) => set({ delivery_challan_no: v })} testid={`${kind}-dc-no`} />
        <MiniField label="Order Date" value={refs.order_date} onChange={(v) => set({ order_date: v })} type="date" testid={`${kind}-order-date`} />
      </div>
    </div>
  );
}

function MiniField({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">{label}</div>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border-2 border-[#fed7aa] focus:border-[#ea580c] px-2 py-1 text-xs outline-none bg-white" data-testid={testid} />
    </label>
  );
}

// ------- Per-invoice Company Override Block (name/address/PAN/bank/logo/signature) -------
export function CompanyOverrideBlock({ override, onChange, kind = "", forceOpen = false, required = false }) {
  const set = (patch) => onChange({ ...override, ...patch });
  const enabled = !!(override && Object.values(override).some((v) => (v || "").toString().trim()));
  const [open, setOpen] = useState(forceOpen || enabled);

  const handleFile = async (field, file) => {
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("File must be under 500KB"); return; }
    const reader = new FileReader();
    reader.onload = () => set({ [field]: reader.result });
    reader.readAsDataURL(file);
  };

  return (
    <div className={`border-2 border-dashed ${required ? "border-[#ea580c] bg-[#fff7ed]" : "border-[#a1a1aa] bg-[#fafaf9]"} p-3`}>
      <button type="button" onClick={() => !forceOpen && setOpen((s) => !s)} className="w-full flex items-center justify-between" data-testid={`${kind}-co-toggle`}>
        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#09090b]">
          {required ? "Bill On Behalf Of (Required)" : "Override Company Details on this Invoice"}{" "}
          {enabled && <span className="text-[#ea580c]">· active</span>}
          {required && !((override.name || "").trim()) && <span className="text-[#dc2626] ml-1">· missing name!</span>}
        </span>
        {!forceOpen && <span className="text-xs font-bold text-[#71717a]">{open ? "− Hide" : "+ Show"}</span>}
      </button>
      {open && (
        <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="space-y-2">
            <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#71717a]">Header {required ? "(required)" : "(leave blank to use GAINA defaults)"}</div>
            <MiniField label={`Company Name${required ? " *" : ""}`} value={override.name} onChange={(v) => set({ name: v })} testid={`${kind}-co-name`} />
            <MiniField label="Address" value={override.address} onChange={(v) => set({ address: v })} />
            <MiniField label="Phone" value={override.phone} onChange={(v) => set({ phone: v })} />
            <MiniField label="Email" value={override.email} onChange={(v) => set({ email: v })} />
            <MiniField label="PAN No." value={override.pan} onChange={(v) => set({ pan: v })} />
          </div>
          <div className="space-y-2">
            <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#71717a]">Bank Account (5 fields)</div>
            <MiniField label="A/c Name" value={override.bank_account_name} onChange={(v) => set({ bank_account_name: v })} />
            <MiniField label="A/c Number" value={override.bank_account_number} onChange={(v) => set({ bank_account_number: v })} />
            <MiniField label="Bank Name" value={override.bank_name} onChange={(v) => set({ bank_name: v })} />
            <MiniField label="Branch" value={override.bank_branch} onChange={(v) => set({ bank_branch: v })} />
            <MiniField label="IFSC" value={override.bank_ifsc} onChange={(v) => set({ bank_ifsc: v })} />
          </div>
          <div className="space-y-2">
            <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#71717a]">Logo & Signature (PNG/JPG · max 500KB)</div>
            <label className="block">
              <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">Upload Logo</div>
              <input type="file" accept="image/png,image/jpeg" onChange={(e) => handleFile("logo_url", e.target.files?.[0])} className="text-xs" data-testid={`${kind}-co-logo`} />
              {override.logo_url && <img src={override.logo_url} alt="logo preview" className="mt-2 h-14 object-contain border border-border" />}
            </label>
            <label className="block">
              <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">Upload Signature</div>
              <input type="file" accept="image/png,image/jpeg" onChange={(e) => handleFile("signature_url", e.target.files?.[0])} className="text-xs" data-testid={`${kind}-co-sig`} />
              {override.signature_url && <img src={override.signature_url} alt="sig preview" className="mt-2 h-12 object-contain border border-border" />}
            </label>
            {enabled && !required && (
              <button type="button" onClick={() => onChange({})} className="text-[10px] font-bold text-[#dc2626] hover:underline">Clear all overrides</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
